import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useGetMedicines } from "hooks/useMedicine";
import { MedicineResponseDto } from "libs/cellera-sdk";
import { useCallback, useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import EmptyMedicines from "./EmptyMedicines";
import LoadingMedicines from "./LoadingMedicines";

const TABLE_HEADERS = [
  { field: "name", headerName: "Product Name" },
  { field: "dosage", headerName: "Dosage" },
  { field: "unit", headerName: "Unit" },
  { field: "quantity", headerName: "Qty" },
] as const;

// Layout constants
const LAYOUT_CONSTANTS = {
  TABLE_HEIGHT: 476,
  CHECKBOX_WIDTH: 50,
  QTY_FIELD_WIDTH: 120,
} as const;

interface MedicineCreatorProps {
  onConfirm: (
    selectedMedicines: Array<{
      medicine: MedicineResponseDto;
      quantity: number;
    }>
  ) => Promise<void>;
  onClose: () => void;
  isAdding: boolean;
}

function MedicineCreator({
  onConfirm,
  onClose,
  isAdding,
}: MedicineCreatorProps) {
  const { colors } = useCelleraTheme();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500);

  const [selectedMedicines, setSelectedMedicines] = useState<
    MedicineResponseDto[]
  >([]);
  const [medicineQuantities, setMedicineQuantities] = useState<
    Record<string, string>
  >({});

  const { data: medicinesResponse, isLoading: isLoadingMedicines } =
    useGetMedicines({
      page: page,
      pageSize: rowsPerPage,
      search: debouncedSearchTerm,
    });

  const medicines = useMemo(() => {
    return medicinesResponse?.medicines || [];
  }, [medicinesResponse]);

  const pagination = useMemo(() => {
    return {
      total: medicinesResponse?.total || 0,
      page: medicinesResponse?.page || 1,
      pageSize: medicinesResponse?.pageSize || 25,
    };
  }, [medicinesResponse]);

  // Calculate select all checkbox state
  const selectedMedicineIds = useMemo(
    () => selectedMedicines.map((m) => m.id),
    [selectedMedicines]
  );

  const selectedOnPage = useMemo(
    () => medicines.filter((m) => selectedMedicineIds.includes(m.id)),
    [medicines, selectedMedicineIds]
  );

  const allSelectedOnPage =
    medicines.length > 0 && selectedOnPage.length === medicines.length;
  const someSelectedOnPage =
    selectedOnPage.length > 0 && selectedOnPage.length < medicines.length;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1); // MUI uses 0-based, API uses 1-based
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page (1-based)
  };

  const handleSelectMedicine = useCallback(
    (medicine: MedicineResponseDto) => {
      const isSelected = selectedMedicines.some((m) => m.id === medicine.id);

      if (isSelected) {
        // Remove medicine and clear its quantity
        setSelectedMedicines((prev) =>
          prev.filter((m) => m.id !== medicine.id)
        );
        setMedicineQuantities((prev) => {
          const newQuantities = { ...prev };
          delete newQuantities[medicine.id];
          return newQuantities;
        });
      } else {
        // Add medicine with default quantity of 1
        setSelectedMedicines((prev) => [...prev, medicine]);
        setMedicineQuantities((prev) => ({
          ...prev,
          [medicine.id]: "1",
        }));
      }
    },
    [selectedMedicines]
  );

  const handleSelectAll = useCallback(() => {
    const currentPageIds = medicines.map((m) => m.id);

    if (allSelectedOnPage) {
      // Deselect all on current page
      setSelectedMedicines((prev) =>
        prev.filter((m) => !currentPageIds.includes(m.id))
      );
      setMedicineQuantities((prev) => {
        const newQuantities = { ...prev };
        currentPageIds.forEach((id) => {
          delete newQuantities[id];
        });
        return newQuantities;
      });
    } else {
      // Select all on current page
      const medicinesToAdd = medicines.filter(
        (medicine) => !selectedMedicineIds.includes(medicine.id)
      );

      setSelectedMedicines((prev) => [...prev, ...medicinesToAdd]);
      setMedicineQuantities((prev) => {
        const newQuantities = { ...prev };
        medicinesToAdd.forEach((medicine) => {
          newQuantities[medicine.id] = "1";
        });
        return newQuantities;
      });
    }
  }, [medicines, allSelectedOnPage, selectedMedicineIds]);

  const handleQuantityChange = useCallback((id: string, value: string) => {
    // Allow empty string or positive numbers
    if (value === "" || /^\d+$/.test(value)) {
      setMedicineQuantities((prev) => ({ ...prev, [id]: value }));
    }
  }, []);

  const handleQuantityBlur = useCallback(
    (id: string) => {
      const currentValue = medicineQuantities[id];

      // If empty or undefined, set to 1
      if (!currentValue || currentValue === "") {
        setMedicineQuantities((prev) => ({ ...prev, [id]: "1" }));
        return;
      }

      const numValue = parseInt(currentValue, 10);

      // If invalid or less than 1, reset to 1
      if (isNaN(numValue) || numValue < 1) {
        setMedicineQuantities((prev) => ({ ...prev, [id]: "1" }));
        return;
      }
    },
    [medicineQuantities]
  );

  const handleConfirm = useCallback(async () => {
    if (selectedMedicines.length === 0) {
      return;
    }

    // Map selected medicines with quantities
    const validSelections = selectedMedicines
      .map((medicine) => {
        const quantity = medicineQuantities[medicine.id];
        const numQuantity = parseInt(quantity);

        // Validate quantity exists and is valid
        if (!quantity || isNaN(numQuantity) || numQuantity <= 0) {
          return null;
        }

        return {
          medicine,
          quantity: numQuantity,
        };
      })
      .filter(
        (item): item is { medicine: MedicineResponseDto; quantity: number } =>
          item !== null
      );

    // Only proceed if we have valid selections
    if (validSelections.length === 0) {
      return;
    }

    // TODO: Implement API call to add medicines to prescription
    console.log("Selected medicines to add:", validSelections);

    await onConfirm(validSelections);
    onClose();
  }, [selectedMedicines, medicineQuantities, onConfirm, onClose]);

  const handleCancel = () => {
    onClose();
  };

  const isConfirmDisabled = selectedMedicines.length === 0 || isAdding;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}
    >
      <Box sx={{ display: "flex", gap: 1, flex: 1, minHeight: 0 }}>
        {/* Medicine Selection Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${colors.border.main}`,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Search Bar */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                gap: 2,
                borderBottom: `1px solid ${colors.border.main}`,
              }}
            >
              <TextField
                fullWidth
                placeholder="Search medicines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: colors.text.secondary }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: colors.background.paper,
                  },
                }}
              />
            </Box>

            {/* Medicines Table */}
            <TableContainer
              sx={{
                flex: 1,
                minHeight: LAYOUT_CONSTANTS.TABLE_HEIGHT,
                maxHeight: LAYOUT_CONSTANTS.TABLE_HEIGHT,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: colors.background.secondary,
                        borderRight: `1px solid ${colors.border.main}`,
                        width: LAYOUT_CONSTANTS.CHECKBOX_WIDTH,
                      }}
                    >
                      <Checkbox
                        checked={allSelectedOnPage}
                        indeterminate={someSelectedOnPage}
                        onChange={handleSelectAll}
                        sx={{ py: 0.5 }}
                      />
                    </TableCell>
                    {TABLE_HEADERS.map((header) => (
                      <TableCell
                        key={header.field}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: colors.background.secondary,
                          borderRight: `1px solid ${colors.border.main}`,
                        }}
                      >
                        {header.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingMedicines ? (
                    <LoadingMedicines />
                  ) : medicines.length === 0 ? (
                    <EmptyMedicines />
                  ) : (
                    medicines.map((medicine) => (
                      <TableRow
                        key={medicine.id}
                        sx={{
                          "&:hover": {
                            backgroundColor: colors.background.secondary,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            borderRight: `1px solid ${colors.border.main}`,
                          }}
                        >
                          <Checkbox
                            checked={selectedMedicineIds.includes(medicine.id)}
                            onChange={() => handleSelectMedicine(medicine)}
                            sx={{ py: 0.5 }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 1,
                            borderRight: `1px solid ${colors.border.main}`,
                            cursor: "pointer",
                          }}
                          onClick={() => handleSelectMedicine(medicine)}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {medicine.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.text.secondary }}
                          >
                            {medicine.code}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 1,
                            borderRight: `1px solid ${colors.border.main}`,
                            cursor: "pointer",
                          }}
                          onClick={() => handleSelectMedicine(medicine)}
                        >
                          {medicine.dosage}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 1,
                            borderRight: `1px solid ${colors.border.main}`,
                            cursor: "pointer",
                          }}
                          onClick={() => handleSelectMedicine(medicine)}
                        >
                          {medicine.unit}
                        </TableCell>
                        <TableCell
                          sx={{
                            py: 0.5,
                            width: LAYOUT_CONSTANTS.QTY_FIELD_WIDTH,
                          }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={medicineQuantities[medicine.id] || ""}
                            onChange={(e) => {
                              handleQuantityChange(medicine.id, e.target.value);
                            }}
                            onBlur={() => handleQuantityBlur(medicine.id)}
                            slotProps={{
                              input: {
                                inputProps: {
                                  min: 1,
                                  step: 1,
                                  style: {
                                    fontSize: 14,
                                  },
                                  disabled: !selectedMedicineIds.includes(
                                    medicine.id
                                  ),
                                },
                              },
                            }}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: colors.border.light,
                                },
                                "&:hover fieldset": {
                                  borderColor: colors.border.main,
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: colors.secondary.main,
                                },
                              },
                              "& input": {
                                py: 0.75,
                                px: 1.2,
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                borderTop: `1px solid ${colors.border.main}`,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                px: 2,
              }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={pagination.total}
                rowsPerPage={rowsPerPage}
                page={page - 1} // Convert to 0-based for MUI
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) =>
                  `Results: ${from}-${to} of ${count}`
                }
                sx={{
                  border: "none",
                  ".MuiTablePagination-toolbar": {
                    paddingLeft: 0,
                  },
                  ".MuiTablePagination-displayedRows": {
                    color: colors.text.secondary,
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.border.main}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={isAdding}
          sx={{
            textTransform: "none",
            borderColor: colors.border.main,
            color: colors.text.primary,
            "&:hover": {
              borderColor: colors.border.dark,
              backgroundColor: colors.background.secondary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          sx={{
            textTransform: "none",
            backgroundColor: colors.secondary.main,
            "&:hover": {
              backgroundColor: colors.secondary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: colors.border.main,
              color: colors.text.disabled,
            },
          }}
        >
          {isAdding ? "Adding..." : "Confirm"}
        </Button>
      </Box>
    </Box>
  );
}

export default MedicineCreator;
